import { IoMdSend } from 'react-icons/io';
import { MdAttachFile } from 'react-icons/md';
import { FaHashtag, FaQuoteLeft, FaLightbulb, FaImage, FaTasks, FaVideo, FaShoppingBag, FaQuoteRight, FaPalette, FaShareAlt, FaChartLine, FaBullhorn, FaCalendarAlt, FaUsers, FaTrophy } from 'react-icons/fa';
import { Button } from '../../../../ui/button';
import { Link } from 'react-router-dom';
import AdvancedToolModal from '../../../../components/Modal/AdvancedToolModal';
import { useState } from 'react';

const ChatInput = ({
    inputValue,
    setInputValue,
    onSendMessage,
    loading,
    isVideoAnalyzing,
    gptLimitReached,
    isEmptyChat = false
}) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);

    // Recommendation badges for empty chat
    const recommendationBadges = [
        { icon: <FaTasks className='text-pink-600'/>, text: "Give me a quote about..." },
        { icon: <FaHashtag className='text-blue-600'/>, text: "Create hashtags for..." },
        { icon: <FaQuoteLeft className='text-green-600'/>, text: "Create a caption for..." },
        { 
            icon: <FaVideo className='text-red-600'/>, 
            text: "Analyze this video", 
        },
        { icon: <FaLightbulb className='text-yellow-600'/>, text: "Give me post ideas about..." },
        { icon: <FaImage className='text-purple-600'/>, text: "Generate an image of..." },
    ];

    // Advanced recommendation cards
    const advancedRecommendations = [
        {
            icon: <FaShoppingBag className='text-2xl text-purple-600' />,
            title: "Product Generator",
            description: "Create stunning branded product images with personalized backgrounds",
            prompt: "Generate a professional product image with custom background"
        },
        {
            icon: <FaQuoteRight className='text-2xl text-blue-600' />,
            title: "Quote Generator",
            description: "Drop text into styled backgrounds with beautiful typography",
            prompt: "Create a quote image with styled background"
        },
        {
            icon: <FaPalette className='text-2xl text-green-600' />,
            title: "Graphic Generator",
            description: "Create awesome graphics to use across all your social media platforms",
            prompt: "Generate a social media graphic"
        },
        {
            icon: <FaShareAlt className='text-2xl text-orange-600' />,
            title: "Social Media Post",
            description: "Design engaging posts for Instagram, Facebook, and Twitter",
            prompt: "Create a social media post design"
        },
        {
            icon: <FaChartLine className='text-2xl text-red-600' />,
            title: "Analytics Visualizer",
            description: "Transform data into beautiful charts and infographics",
            prompt: "Create an analytics visualization"
        },
        {
            icon: <FaBullhorn className='text-2xl text-yellow-600' />,
            title: "Marketing Campaign",
            description: "Design complete marketing campaign visuals and copy",
            prompt: "Generate marketing campaign materials"
        },
        {
            icon: <FaCalendarAlt className='text-2xl text-indigo-600' />,
            title: "Event Promoter",
            description: "Create event posters, flyers, and promotional materials",
            prompt: "Design event promotional materials"
        },
        {
            icon: <FaUsers className='text-2xl text-teal-600' />,
            title: "Team Collaboration",
            description: "Generate team photos, organizational charts, and collaboration graphics",
            prompt: "Create team collaboration graphics"
        },
        {
            icon: <FaTrophy className='text-2xl text-amber-600' />,
            title: "Achievement Showcase",
            description: "Design certificates, awards, and achievement graphics",
            prompt: "Generate achievement showcase graphics"
        }
    ];

    const handleCardClick = (card) => {
        setSelectedCard(card);
        setShowConfirmModal(true);
    };

    const handleConfirmModal = (enhancedPrompt) => {
        if (enhancedPrompt) {
            setInputValue(enhancedPrompt);
            setShowConfirmModal(false);
            setSelectedCard(null);
        }
    };

    const handleNextCards = () => {
        setCurrentCardIndex(prev => Math.min(prev + 3, advancedRecommendations.length - 3));
    };

    const handlePrevCards = () => {
        setCurrentCardIndex(prev => Math.max(prev - 3, 0));
    };

    const visibleCards = advancedRecommendations.slice(currentCardIndex, currentCardIndex + 3);
    const canGoNext = currentCardIndex + 3 < advancedRecommendations.length;
    const canGoPrev = currentCardIndex > 0;

    // Render limit reached message
    const renderLimitReachedMessage = () => {
        if (!gptLimitReached) return null;
        
        return (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4 mt-2">
                <p className="font-medium">You've reached your GPT message limit!</p>
                <p className="mt-1">To continue using the AI assistant, please add more credits to your account.</p>
                <Link to="/dashboard/settings/subscription" className="mt-2 inline-block bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700">
                    Upgrade Subscription
                </Link>
            </div>
        );
    };

    if (isEmptyChat) {
        // Empty chat with centered input
        return (
            <div className="flex flex-col items-center md:justify-center h-full p-4 overflow-auto">
                <div className="w-full max-w-2xl">
                    <h2 className="text-2xl font-bold mb-6 text-center">What can I help you with?</h2>
                    
                    {renderLimitReachedMessage()}
                    
                    {/* Centered multiline textarea */}
                    <form onSubmit={onSendMessage} className='--theme-colors'>
                        <div className="relative border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                            <textarea
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (inputValue.trim() && !loading && !isVideoAnalyzing && !gptLimitReached) {
                                            onSendMessage(e);
                                        }
                                    }
                                }}
                                placeholder={gptLimitReached ? "You've reached your GPT limit. Upgrade to continue..." : "Ask FrenzyAI what to build..."}
                                className="w-full py-3 px-4 outline-none min-h-[120px] resize-none rounded-md bg-transparent"
                                rows={4}
                                disabled={loading || isVideoAnalyzing || gptLimitReached}
                            />
                            
                            <div className="absolute bottom-2 right-2 flex items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="text-gray-500 p-2 rounded-md hover:bg-gray-100"
                                    disabled={loading || isVideoAnalyzing || gptLimitReached}
                                >
                                    <MdAttachFile className="h-5 w-5" />
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    disabled={!inputValue.trim() || loading || isVideoAnalyzing || gptLimitReached}
                                >
                                    <IoMdSend className="h-5 w-5" />
                                </Button>
                            </div>
                        </div>
                    </form>
                    
                    {/* Recommendation badges */}
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                        {recommendationBadges.map((badge, index) => (
                            <Button
                                key={index}
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    if (badge.text === "Generate an image of") {
                                        setInputValue("Generate an image of a stunning sunset over mountains");
                                    } else {
                                        setInputValue(`Help me ${badge.text.toLowerCase()}`);
                                    }
                                }}
                                className={`${badge.special ? badge.className : '--theme-colors'} hover:shadow-md transition-shadow duration-200 hover:border-blue-300`}
                                disabled={loading || isVideoAnalyzing || gptLimitReached}
                            >
                                <span className={`${badge.special ? 'text-red-600' : 'text-gray-500'}`}>{badge.icon}</span>
                                {badge.text}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Advanced recommendation cards - outside the constrained container */}
                <div className="w-full mt-8">
                    <h3 className="text-lg font-semibold mb-4 text-center">Advanced Tools</h3>
                    <div className="relative max-w-6xl mx-auto">
                        {/* Navigation arrows */}
                        {canGoPrev && (
                            <button
                                onClick={handlePrevCards}
                                className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow duration-200"
                                disabled={loading || isVideoAnalyzing || gptLimitReached}
                            >
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}
                        
                        {canGoNext && (
                            <button
                                onClick={handleNextCards}
                                className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow duration-200"
                                disabled={loading || isVideoAnalyzing || gptLimitReached}
                            >
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}

                        {/* Cards container */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-8">
                            {visibleCards.map((card, index) => (
                                <div
                                    key={currentCardIndex + index}
                                    onClick={() => handleCardClick(card)}
                                    className="--theme-colors border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow duration-200 hover:border-blue-300"
                                    disabled={loading || isVideoAnalyzing || gptLimitReached}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-1">
                                            {card.icon}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-900 mb-1">{card.title}</h4>
                                            <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Scroll indicator */}
                        <div className="flex justify-center mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{currentCardIndex + 1}-{Math.min(currentCardIndex + 3, advancedRecommendations.length)} of {advancedRecommendations.length}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Advanced Tool Modal */}
                <AdvancedToolModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={handleConfirmModal}
                    selectedCard={selectedCard}
                />
            </div>
        );
    }

    // Input area for active chat (at bottom)
    return (
        <div className="border-t border-gray-200 p-2 md:p-4 --theme-colors">
            {renderLimitReachedMessage()}
            <form onSubmit={onSendMessage} className="flex items-center gap-2 border border-gray-300 rounded-md pr-1 pl-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (inputValue.trim() && !loading && !isVideoAnalyzing && !gptLimitReached) {
                                onSendMessage(e);
                            }
                        }
                    }}
                    placeholder={gptLimitReached ? "You've reached your GPT limit. Upgrade to continue..." : "Ask FrenzyAI what to do..."}
                    className="flex-1 py-2 text-sm bg-transparent md:text-base outline-none border-none"
                    disabled={loading || isVideoAnalyzing || gptLimitReached}
                />
                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        className="text-gray-500 p-1 md:p-2 rounded-md hover:bg-gray-100"
                        disabled={loading || isVideoAnalyzing || gptLimitReached}
                    >
                        <MdAttachFile className="h-4 w-4 md:h-5 md:w-5" />
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        className="bg-blue-600 text-white p-1 md:p-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        disabled={!inputValue.trim() || loading || isVideoAnalyzing || gptLimitReached}
                    >
                        <IoMdSend className="h-4 w-4 md:h-5 md:w-5" />
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default ChatInput; 